import React, { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Avatar from "@mui/material/Avatar";
import AvatarGroup from "@mui/material/AvatarGroup";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";

import WorkspaceService from "services/workspace-service";
import ProfileService from "services/profile-service";
import SnapshotService from "services/snapshot-service";

import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";

/**
 * ⚡ SocialStudioModal – clean version with only the snapshot table
 */
function SocialStudioModal() {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [snapshotToDelete, setSnapshotToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ─────────────────────────────────────────────
  // Fetch Snapshot List
  // ─────────────────────────────────────────────
  const fetchSnapshots = async () => {
    try {
      setLoading(true);
      const data = await WorkspaceService.fetchWorkspaceSnapshots();

      const enriched = await Promise.all(
        data.map(async (snapshot) => {
          const primaryList = Array.isArray(snapshot.primary_profile)
            ? snapshot.primary_profile
            : [snapshot.primary_profile].filter(Boolean);

          const secondaryList = Array.isArray(snapshot.secondary_profiles)
            ? snapshot.secondary_profiles
            : [snapshot.secondary_profiles].filter(Boolean);

          const groupedPrimary = groupByPlatform(primaryList);
          const groupedSecondary = groupByPlatform(secondaryList);

          const primaryResp = await Promise.all(
            Object.entries(groupedPrimary).map(([platform, ids]) =>
              ProfileService.fetchProfile(platform, ids)
            )
          );

          const secondaryResp = await Promise.all(
            Object.entries(groupedSecondary).map(([platform, ids]) =>
              ProfileService.fetchProfile(platform, ids)
            )
          );

          return {
            ...snapshot,
            primaryProfile: primaryResp.flat(),
            secondaryProfiles: secondaryResp.flat(),
          };
        })
      );

      setSnapshots(enriched);
    } catch (err) {
      console.error("❌ Error loading snapshots:", err);
      setError("Failed to load snapshots.");
    } finally {
      setLoading(false);
    }
  };

  const groupByPlatform = (profiles) =>
    profiles.reduce((acc, { platform, profile_id }) => {
      if (!acc[platform]) acc[platform] = [];
      acc[platform].push(profile_id);
      return acc;
    }, {});

  useEffect(() => {
    fetchSnapshots();
  }, []);

  // ─────────────────────────────────────────────
  // Delete
  // ─────────────────────────────────────────────
  const handleDeleteClick = (snapshot) => {
    setSnapshotToDelete(snapshot);
    setIsConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!snapshotToDelete) return;

    try {
      setDeleting(true);
      // 🗑️ Soft delete: Update status to 'inactive' instead of hard delete
      await WorkspaceService.updateWorkspaceSnapshot(snapshotToDelete._id, { status: "inactive" });
      setIsConfirmDialogOpen(false);
      fetchSnapshots();
    } catch (err) {
      console.error("❌ Failed to delete snapshot:", err);
    } finally {
      setSnapshotToDelete(null);
      setDeleting(false);
    }
  };

  // ─────────────────────────────────────────────
  // Avatar Renderer
  // ─────────────────────────────────────────────
  const renderAvatars = (profiles) => {
    if (!Array.isArray(profiles)) return null;
    return (
      <MDBox display="flex" justifyContent="left" alignItems="left">
        <AvatarGroup max={4} sx={{ "& .MuiAvatar-root": { width: 28, height: 28 } }}>
          {profiles.map((profile, idx) => (
            <Tooltip key={idx} title={profile.name || "Profile"} arrow>
              <Avatar
                src={profile.profilePictureUrl || ""}
                alt={profile.name || "P"}
                sx={{ width: 28, height: 28 }}
              />
            </Tooltip>
          ))}
        </AvatarGroup>
      </MDBox>
    );
  };

  // ─────────────────────────────────────────────
  // Table Columns
  // ─────────────────────────────────────────────
  const columns = [
    {
      Header: (
        <MDBox py={1} px={2} pl={3} sx={{ minWidth: "250px" }}>
          <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
            NAME
          </MDTypography>
        </MDBox>
      ),
      accessor: "snapshotName",
      align: "left",
    },
    {
      Header: (
        <MDBox py={1} px={2} pl={3}>
          <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
            TYPE
          </MDTypography>
        </MDBox>
      ),
      accessor: "snapshotType",
      align: "left",
    },
    {
      Header: (
        <MDBox py={1} px={2} pl={3}>
          <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
            PRIMARY
          </MDTypography>
        </MDBox>
      ),
      accessor: "primaryProfile",
      align: "left",
    },
    {
      Header: (
        <MDBox py={1} px={2} pl={3}>
          <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
            SECONDARY
          </MDTypography>
        </MDBox>
      ),
      accessor: "secondaryProfiles",
      align: "left",
    },
    {
      Header: (
        <MDBox py={1} px={2} pl={3}>
          <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
            NEXT RUN
          </MDTypography>
        </MDBox>
      ),
      accessor: "nextRunAt",
      align: "left",
    },
    {
      Header: (
        <MDBox py={1} px={2}>
          <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
            ACTION
          </MDTypography>
        </MDBox>
      ),
      accessor: "action",
      align: "center",
    },
  ];


  // ❌ REMOVE Edit, Download buttons
  // Only Delete button remains
  const rows = snapshots.map((snapshot) => {
    const isInactive = snapshot.status?.toLowerCase() === "inactive";
    const rowOpacity = isInactive ? 0.5 : 1;

    // Wrap row content function (Opacity only, no tooltip on data cells)
    const wrapCell = (content) => (
      <MDBox sx={{ opacity: rowOpacity }}>
        {content}
      </MDBox>
    );

    return {
      snapshotName: wrapCell(snapshot.snapshot_name),
      snapshotType: wrapCell(snapshot.snapshot_type || "—"),
      primaryProfile: wrapCell(renderAvatars(snapshot.primaryProfile)),
      secondaryProfiles: wrapCell(
        snapshot.secondaryProfiles && snapshot.secondaryProfiles.length > 0
          ? renderAvatars(snapshot.secondaryProfiles)
          : "—"
      ),
      nextRunAt: wrapCell(
        snapshot.nextRunAt
          ? new Date(snapshot.nextRunAt).toLocaleDateString("en-GB")
          : "—"
      ),
      action: isInactive ? (
        <Tooltip title="Deleted Snapshot" arrow placement="bottom">
          <MDTypography variant="caption" color="text">Deleted</MDTypography>
        </Tooltip>
      ) : (
        <IconButton
          size="small"
          onClick={() => handleDeleteClick(snapshot)}
        >
          <DeleteIcon color="error" fontSize="small" />
        </IconButton>
      ),
    };
  });

  return (
    <MDBox p={2} sx={{ width: "100%" }}>
      <Card sx={{ p: 2 }}>
        <MDBox
          mx={2}
          mt={-5}
          mb={2}
          py={1}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          flexDirection="row"
        >
          <MDTypography variant="h6" color="white">
            Snapshot Library
          </MDTypography>
        </MDBox>

        {/* Loader */}
        {loading ? (
          <MDBox display="flex" justifyContent="center" py={5}>
            <CircularProgress color="accent" />
          </MDBox>
        ) : error ? (
          <MDTypography color="error">{error}</MDTypography>
        ) : (
          <DataTable
            canSearch={false}
            isSorted={false}
            showTotalEntries={false}
            noEndBorder
            entriesPerPage={false}
            table={{ columns, rows }}
          />
        )}
      </Card>

      {/* Delete Confirmation */}
      <Dialog open={isConfirmDialogOpen} onClose={() => setIsConfirmDialogOpen(false)}
        sx={{ zIndex: 2000 }}
      >
        <DialogTitle>Delete Snapshot</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete snapshot{" "}
            <strong>{snapshotToDelete?.snapshot_name}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsConfirmDialogOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}

export default SocialStudioModal;
